import { useAuth0 } from '@auth0/auth0-react';
import { useActions, useStore } from '../store/hooks';
import { PolicyBundle, User } from '../utils/types/Entity';
import { AuthAPI, PolicyBundleAPI, UserAPI } from '../api';
import { useEffect, useState } from 'react';

type ProtectUserHook = {
  user?: User;
  policyBundles?: PolicyBundle[];
  ready: boolean;
  error: boolean;
};

const useProtectUser = (): ProtectUserHook => {
  const { isLoading, isAuthenticated, getAccessTokenSilently } = useAuth0();
  const {
    user: { user },
    policyBundles: { policyBundles, ready: policyBundlesReady },
  } = useStore();
  const dispatch = useActions();
  const [errorFlag, setErrorFlag] = useState<boolean>(false);
  const [newUserFlag, setNewUserFlag] = useState<boolean>(false);

  useEffect(() => {
    const getUserAndBundles = async () => {
      if (isLoading || !isAuthenticated || errorFlag) {
        return;
      }

      if (!user.id) {
        try {
          const token = await getAccessTokenSilently();
          const _user = await UserAPI.getUser(token);
          if (!_user) {
            void AuthAPI.verifyUserEmail(token, navigator.language);
            setNewUserFlag(true);
            return;
          }
          void AuthAPI.verifyUserEmail(token, _user.langLocale);
          dispatch.setUser(_user);
        } catch (e) {
          console.error(e);
          setErrorFlag(true);
        }

        return;
      }

      if (!policyBundlesReady) {
        try {
          const token = await getAccessTokenSilently({ cacheMode: 'off' });
          const policyBundles = await PolicyBundleAPI.getPolicyBundles(token);
          dispatch.setPolicyBundles(policyBundles);
          dispatch.setPolicyBundlesReady();
        } catch (e) {
          console.error(e);
          setErrorFlag(true);
        }
      }
    };
    void getUserAndBundles();
  }, [dispatch, user, getAccessTokenSilently, isLoading, isAuthenticated]);

  if (errorFlag) {
    return { ready: true, error: true };
  }

  if (isLoading) {
    return { ready: false, error: false };
  }

  if (!isAuthenticated) {
    return { ready: true, error: false };
  }

  if (!newUserFlag && !policyBundlesReady) {
    return { ready: false, error: false };
  }

  return { ready: true, error: false, user, policyBundles };
};

export default useProtectUser;
